import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTRPCAPI } from '../../../../trpc/trpc';
import type { TargetRow, TargetSearchPage } from '../outreachTypes';
import type { SearchableTargetKind } from '../find/findKinds';
import { useRetry } from './useRetry';

export interface TargetFilters {
  kind: SearchableTargetKind;
  catalogItemUuid: string | null;
  q: string;
  country: string | null;
  minFollowers: number | null;
  maxFollowers: number | null;
  onlyWithEmail: boolean;
  includeFlagged: boolean;
}

/** `outreach.targets.search` (frame c1): 50 rows a page, the next page on "Show more". */
export function useTargetSearch(accountId: number, filters: TargetFilters) {
  const trpcAPI = useTRPCAPI();
  const input = {
    accountId,
    kind: filters.kind,
    catalogItemUuid: filters.catalogItemUuid,
    q: filters.q.trim() === '' ? null : filters.q.trim(),
    country: filters.country,
    minFollowers: filters.minFollowers,
    maxFollowers: filters.maxFollowers,
    onlyWithEmail: filters.onlyWithEmail,
    includeFlagged: filters.includeFlagged,
  };
  const query = useInfiniteQuery(
    trpcAPI.outreach.targets.search.infiniteQueryOptions(input, {
      getNextPageParam: (page: TargetSearchPage) => page.nextCursor ?? undefined,
      retry: false,
    })
  );
  const pages = query.data?.pages as TargetSearchPage[] | undefined;
  const rows = useMemo<TargetRow[]>(() => (pages ?? []).flatMap((page) => page.rows), [pages]);
  const first = pages?.[0];
  const retry = useRetry(query.refetch);
  return {
    rows,
    total: first?.total ?? 0,
    cappedAt: first?.cappedAt ?? null,
    isLoading: query.isLoading,
    error: query.error,
    retry,
    hasMore: query.hasNextPage,
    loadMore: query.fetchNextPage,
    isLoadingMore: query.isFetchingNextPage,
  };
}
